import { createAsyncThunk } from '@reduxjs/toolkit';
import { classToPlain } from 'class-transformer';

import { AppError } from '@vk-hr-tek/core/error';
import {
  setDownloadWaiting,
  pollDownloadStatus,
} from '@vk-hr-tek/core/download-notification';
import { FormatService } from '@vk-hr-tek/core/format';

import { CreateEventBatchDocumentZipResponse } from '@app/gen/events';
import {
  CompanyPolicyXlsxResponse,
  CompanyPolicyXlsxRequest,
} from '@app/gen/policy';

import { ThunkExtra } from '../../../app/store';
import { PolicyDownloadService, PolicyListService } from '../../services';
import {
  DownloadPolicyVersionDto,
  DownloadEmployeePolicyDto,
  GetPolicyDto,
} from '../../dto';
import { setFilters } from '../actions';

export const startDownloadAllAccepted = createAsyncThunk<
  CreateEventBatchDocumentZipResponse,
  DownloadPolicyVersionDto,
  ThunkExtra
>(
  'policy/startDownloadAllAccepted',
  async (
    downloadStartDto: DownloadPolicyVersionDto,
    { rejectWithValue, getState, dispatch, extra: { inject } },
  ) => {
    try {
      const service = inject(PolicyDownloadService);

      const count = getState().policy.detail.employees.entities.length;

      const result =
        await service.startDownloadAllAcceptedPolicies(downloadStartDto);

      await dispatch(
        setDownloadWaiting({
          id: result.download_id,
          count: count,
        }),
      );

      dispatch(pollDownloadStatus(result));

      return result;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const startDownloadEmployeeAccepted = createAsyncThunk<
  CreateEventBatchDocumentZipResponse,
  DownloadEmployeePolicyDto,
  ThunkExtra
>(
  'policy/startDownloadEmployeeAccepted',
  async (
    downloadStartDto: DownloadEmployeePolicyDto,
    { rejectWithValue, dispatch, extra: { inject } },
  ) => {
    try {
      const service = inject(PolicyDownloadService);

      const result =
        await service.startDownloadEmployeeAcceptedPolicies(downloadStartDto);

      await dispatch(
        setDownloadWaiting({
          id: result.download_id,
          count: 1,
        }),
      );

      dispatch(pollDownloadStatus(result));

      return result;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const startDownloadXlsx = createAsyncThunk<
  CompanyPolicyXlsxResponse,
  {
    dto: GetPolicyDto;
    options?: Pick<
      CompanyPolicyXlsxRequest,
      'only_not_accepted_employees' | 'policy_id'
    >;
  },
  ThunkExtra
>(
  'policy/startDownloadXlsx',
  async (
    { dto, options },
    { rejectWithValue, getState, dispatch, extra: { inject } },
  ) => {
    try {
      const service = inject(PolicyDownloadService);
      const policiesService = inject(PolicyListService);
      const format = inject(FormatService);

      const state = getState().policy;

      const {
        totalCount,
        totalVersionCount,
        totalUnacceptedCount,
        totalUnacceptedVersionCount,
      } = state;

      let filters = state.filters;

      if (!filters) {
        filters = await policiesService.getFilters();
        dispatch(setFilters(filters));
      }

      const result = await service.startDownloadXlsx(dto, filters, options);

      const isSinglePolicy = !!options?.policy_id;
      const isOnlyNotAccepted = !!options?.only_not_accepted_employees;

      const documentCount = isOnlyNotAccepted
        ? totalUnacceptedCount
        : totalCount;
      const versionCount = isOnlyNotAccepted
        ? totalUnacceptedVersionCount
        : totalVersionCount;

      const message = isSinglePolicy
        ? '1 документ'
        : [
            documentCount &&
              format.pluralize(documentCount, [
                'документ',
                'документа',
                'документов',
              ]),
            versionCount &&
              format.pluralize(versionCount, ['версия', 'версии', 'версий']),
          ]
            .filter(Boolean)
            .join(', ');

      await dispatch(
        setDownloadWaiting({
          id: result.download_id,
          count: totalCount || 0,
          text: {
            loading: `Создаем отчет в формате .xlsx (${message})`,
            complete: `Отчет в формате .xlsx создан (${message})`,
            failed: 'Ошибка создания отчета',
          },
        }),
      );

      dispatch(pollDownloadStatus(result));

      return result;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);
